/**
 * PIEMotivationSurveyWidget — quarterly motivation survey, slotted on
 * the Dashboard. Three sliders (Intrinsic / Extrinsic / Manager support)
 * → updates persona M factors → PE/LEI recompute on next refresh.
 *
 * Three states:
 *   • Not due (submitted within last 90 days) → renders nothing.
 *   • Due → expanded card with three sliders + reflection text.
 *   • Just submitted → tiny ack strip with new M_index value.
 */
import React, { useEffect, useState } from 'react';
import { Battery, Send, CheckCircle2, Heart, ShieldCheck, Briefcase } from 'lucide-react';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';
import { Perf } from './PIEDisplayMode';

const FACTORS = [
  {
    key: 'intrinsic',
    label: 'Intrinsic',
    icon: Heart,
    hint: "How much energy do you get from the work itself this quarter?",
    anchors: { 0.0: 'Drained', 0.5: 'Steady', 1.0: 'Energised' },
  },
  {
    key: 'extrinsic',
    label: 'Extrinsic',
    icon: Briefcase,
    hint: 'Compensation, recognition, advancement — does the structure work for you?',
    anchors: { 0.0: 'Misaligned', 0.5: 'Adequate', 1.0: 'Strong' },
  },
  {
    key: 'manager_support',
    label: 'Manager support',
    icon: ShieldCheck,
    hint: 'How present + helpful has your manager been this quarter?',
    anchors: { 0.0: 'Absent', 0.5: 'Mixed', 1.0: 'Present' },
  },
];

function nearestAnchor(v) {
  // Pick the closest of {0, 0.5, 1.0} for label rendering.
  if (v < 0.25) return 0.0;
  if (v < 0.75) return 0.5;
  return 1.0;
}

export default function PIEMotivationSurveyWidget() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(null);
  const [values, setValues] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    pieApi.motivationSurveyCurrent()
      .then((d) => {
        setState(d);
        setValues(d?.defaults || { intrinsic: 0.6, extrinsic: 0.6, manager_support: 0.6 });
      })
      .catch((e) => logger.warn('motivationSurveyCurrent failed', e));
  }, []);

  if (!state) return null;
  if (justSubmitted) {
    return (
      <div
        className="pie-card"
        style={{
          marginBottom: 24, padding: '14px 18px',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          borderLeft: '3px solid var(--pie-success, #16A34A)',
        }}
        data-testid="pie-motivation-survey-ack"
      >
        <CheckCircle2 size={18} style={{ color: 'var(--pie-success, #16A34A)', flexShrink: 0 }} aria-hidden />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pie-text)' }}>
            Quarterly motivation captured — thanks.
          </div>
          <div style={{ fontSize: 12, color: 'var(--pie-text-secondary)', marginTop: 2 }}>
            New M index: <strong><Perf value={justSubmitted.m_index || 0} kind="pct01" /></strong>
            {' '}· {justSubmitted.quarter} · We&apos;ll ask again in 90 days.
          </div>
        </div>
      </div>
    );
  }
  if (!state.due) return null;

  const onSubmit = async () => {
    setBusy(true);
    try {
      const result = await pieApi.motivationSurveySubmit({
        intrinsic: values.intrinsic,
        extrinsic: values.extrinsic,
        manager_support: values.manager_support,
        notes: notes.trim() || null,
      });
      setJustSubmitted(result);
    } catch (e) {
      logger.error('motivationSurveySubmit failed', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="pie-card pie-card-elevated"
      style={{ marginBottom: 24, padding: '20px 22px' }}
      data-testid="pie-motivation-survey-prompt"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Battery size={16} style={{ color: 'var(--pie-accent)' }} aria-hidden />
        <div className="pie-eyebrow" style={{ background: 'transparent', padding: 0, color: 'var(--pie-accent)' }}>
          Quarterly motivation check · {state.quarter}
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--pie-text)', marginBottom: 4 }}>
        How&apos;s your motivation actually trending?
      </div>
      <div style={{ fontSize: 12, color: 'var(--pie-text-secondary)', marginBottom: 18 }}>
        Once a quarter. The numbers re-calibrate the M factor in your PE math, so be honest — there&apos;s no merit badge for &ldquo;all 100%&rdquo;.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {FACTORS.map((f) => (
          <SliderRow
            key={f.key}
            factor={f}
            value={values?.[f.key] ?? 0.5}
            onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
          />
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <label
          htmlFor="motivation-survey-notes"
          style={{ fontSize: 12, color: 'var(--pie-text-secondary)', display: 'block', marginBottom: 6 }}
        >
          Anything you want the engine to know? (optional)
        </label>
        <textarea
          id="motivation-survey-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. New manager since April — adjusting."
          rows={2}
          data-testid="pie-motivation-survey-notes"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--pie-border)', resize: 'vertical',
            fontSize: 13, color: 'var(--pie-text)', background: 'var(--pie-surface)',
            fontFamily: 'inherit',
          }}
        />
      </div>

      <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          className="pie-btn pie-btn-primary"
          data-testid="pie-motivation-survey-submit-btn"
        >
          <Send size={13} /> {busy ? 'Saving…' : 'Submit motivation read'}
        </button>
      </div>
    </section>
  );
}

function SliderRow({ factor, value, onChange }) {
  const Icon = factor.icon;
  const anchor = nearestAnchor(value);
  const anchorLabel = factor.anchors[anchor] || '';
  return (
    <div data-testid={`pie-motivation-survey-row-${factor.key}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon size={14} aria-hidden style={{ color: 'var(--pie-accent)' }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pie-text)' }}>
          {factor.label}
        </div>
        <div className="pie-mono" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--pie-text-secondary)' }}>
          <Perf value={value} kind="pct01" />
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--pie-text-muted)', marginBottom: 6 }}>
        {factor.hint}
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={`pie-motivation-survey-slider-${factor.key}`}
        style={{ width: '100%' }}
      />
      <div style={{ fontSize: 10, color: 'var(--pie-text-muted)', marginTop: 4 }}>
        {anchorLabel}
      </div>
    </div>
  );
}
